import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardWrapper from "../../components/DashboardWrapper";
import orderService from "../../services/orderService";
import Loader from "../../components/Loader";
import Swal from "sweetalert2";

export default function DisplayParticularOrderDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const invoiceRef = useRef(null);

  const orderStatusOptions = ["pending", "processing", "shipped", "delivered", "cancelled"];
  const paymentStatusOptions = ["pending", "paid", "failed", "refunded"];

  const handleStatusChange = async (field, value) => {
  const result = await Swal.fire({
      title: "Are you sure?",
      text: `Change ${field.replace("_", " ")} to "${value}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, update it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#FB6C22",
    });

    if (!result.isConfirmed) return;

    try {
      await orderService.updateStatus(order.id, { [field]: value });
      setOrder((prev) => ({ ...prev, [field]: value }));
      Swal.fire({
        title: "Success",
        text: "Banner updated successfully!",
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#FB6C22",
      });

    } catch (error) {
      Swal.fire("Error!", "Failed to update status.", "error");
    }
  };

  const fetchOrderDetails = async () => {
    try {
      const response = await orderService.getOrderById(id);
      setOrder({
        ...response.data.order,
        order_products: response.data.order.order_products || [],
      });
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  const handleDownloadPDF = () => {
    if (!invoiceRef.current) return;

    const printWindow = window.open("", "_blank", "width=900,height=900");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice ${order.invoice_id}</title>
          <meta charset="utf-8" />
          <style>
            body { font-family: Arial; padding: 40px; background: white; color: black; }
            .invoice { max-width: 800px; margin: auto; border: 1px solid #ddd; padding: 30px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ccc; padding: 10px; }
            th { background: #f8f9fa; }
            .no-print, .no-print * { display: none !important; }
          </style>
        </head>
        <body>${invoiceRef.current.innerHTML}</body>
      </html>
    `);

    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  return (
    <DashboardWrapper>
      <div className="container my-5">
        {loading && <Loader minHeight={200} />}

        {!loading && order && (
          <div>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 no-print">
              <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                Back
              </button>

              <div>
                <button onClick={handleDownloadPDF} className="btn btn-success me-3">
                  Download PDF
                </button>
                <button onClick={() => window.print()} className="btn btn-outline-secondary">
                  Print
                </button>
              </div>
            </div>

            {/* Invoice */}
            <div ref={invoiceRef} className="invoice border rounded shadow p-4 bg-white">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h2 className="fw-bold">INVOICE</h2>
                  <p className="text-muted mb-1">
                    Invoice ID: <strong>{order.invoice_id}</strong>
                  </p>
                  <p className="text-muted mb-1">
                    Status: <strong className="text-primary text-capitalize">{order.order_status}</strong>
                  </p>
                  <p className="text-muted">
                    Date: {new Date(order.created_at).toLocaleDateString("en-IN")}
                  </p>
                </div>

                <div className="text-end">
                  <h5 className="fw-bold">From:</h5>
                  <p className="text-muted mb-1">VITAURA</p>
                  <p className="text-muted mb-1">123 Business Street, City</p>
                  <p className="text-muted mb-1">support@anuradha.com</p>
                  <p className="text-muted mb-1">+91 98765 43210</p>
                </div>
              </div>

              <hr />

              <div className="row mt-4">
                <div className="col-md-6">
                  <h5 className="fw-bold">Bill To:</h5>
                  <p className="text-muted mb-1">{order.user?.username}</p>
                  <p className="text-muted mb-1">{order.user?.email}</p>
                  <p className="text-muted mb-1">{order.user?.phone_number}</p>
                </div>

                <div className="col-md-6 text-md-end">
                  <h5 className="fw-bold">Ship To:</h5>
                  <p className="text-muted mb-1">{order.order_address?.name}</p>
                  <p className="text-muted mb-1">{order.order_address?.address}</p>
                  <p className="text-muted mb-1">
                    {order.order_address?.city}, {order.order_address?.state} - {order.order_address?.zip}
                  </p>
                  <p className="text-muted mb-1">{order.order_address?.phone}</p>
                </div>
              </div>

              <hr />

              <h5 className="fw-bold mt-4">Order Items</h5>
              <div className="table-responsive">
              <table className="table table-striped mt-3">
                <thead className="table-dark">
                  <tr>
                    <th>Product</th>
                    <th>Unit Price</th>
                    <th>Qty</th>
                    <th>Sku</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.order_products.map((item) => (
                    <tr key={item.id}>
                      <td>{item.product_name}</td>
                      <td>₹ {parseFloat(item.unit_price).toFixed(2)}</td>
                      <td>{item.qty}</td>
                      <td>{item.product?.sku}</td>
                      <td>₹ {(item.unit_price * item.qty).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              {/* Status Dropdowns (UI UNCHANGED) */}
              <div className="row mt-4">
                <div className="col-md-7 no-print">
                  <label className="fw-bold">Order Status</label>
                  <select
                    className="form-select mb-3"
                    value={order.order_status}
                    onChange={(e) => handleStatusChange("order_status", e.target.value)}
                  >
                    {orderStatusOptions.map((st) => (
                      <option key={st} value={st}>
                        {st.charAt(0).toUpperCase() + st.slice(1)}
                      </option>
                    ))}
                  </select>

                  <label className="fw-bold">Payment Status</label>
                  <select
                    className="form-select"
                    value={order.payment_status}
                    onChange={(e) => handleStatusChange("payment_status", e.target.value)}
                  >
                    {paymentStatusOptions.map((st) => (
                      <option key={st} value={st}>
                        {st.charAt(0).toUpperCase() + st.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-5">
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <th>Subtotal:</th>
                        <td className="text-end">₹ {parseFloat(order.sub_total).toFixed(2)}</td>
                      </tr>
                      <tr>
                        <th>Shipping:</th>
                        <td className="text-end">
                          ₹ {parseFloat(order.shipping_method?.cost || 0).toFixed(2)}
                        </td>
                      </tr>
                      <tr>
                        <th>Payment Method:</th>
                        <td className="text-end">{order.payment_method.toUpperCase()}</td>
                      </tr>
                      <tr className="fw-bold fs-5">
                        <th>Grand Total:</th>
                        <td className="text-end">
                          ₹ {(Number(order.amount)).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="text-center mt-5">
                <p className="text-muted">Thank you for shopping with us!</p>
                <small>For support: support@anuradha.com</small>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @media print {
          body * {
            visibility: hidden;
          }

          .invoice,
          .invoice * {
            visibility: visible;
          }

          .invoice {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }

          .no-print,
          .no-print * {
            display: none !important;
          }
        }
      `}</style>
    </DashboardWrapper>
  );
}
